import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';

import { DocumentsActions } from 'state/modules/documents';
import { TextField } from 'components/FormFields';

import { BaseModal } from 'components/UI';
import { removeEmpty } from 'utils/utils';

const UpdateDocumentTypeModal = ({ id, documentType, open, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { documentTypeLoading, documentTypeSuccess } = useSelector((state) => state.documents);

  // Validation schema of update user form
  const UpdateTimesSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    id_slug: Yup.string().matches('^[_a-zA-Z0-9]*$', i18n.t('common.id_slug_match')),
  });

  // Initials values of the form
  const initialValues = {
    name: documentType?.name || '',
    id_slug: documentType?.id_slug || '',
  };

  useEffect(() => {
    if (documentTypeSuccess) {
      handleClick();
      dispatch(DocumentsActions.resetUpdateDocumentType());
    }
  }, [documentTypeSuccess, handleClick, dispatch]);

  /**
   * Handle form submit
   * @param {object} values Object with the value of fields of the form
   */
  const onSubmit = (values) => {
    if (id) {
      const payload = { ...values, id };
      return dispatch(DocumentsActions.updateDocumentType(removeEmpty(payload)));
    }
    return dispatch(DocumentsActions.updateDocumentType(removeEmpty(values)));
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={handleClick}
      closeButtonType="icon"
      enableCloseButton={false}
      loading={documentTypeLoading}
      title={
        id ? i18n.t('documents.update_document_type') : i18n.t('documents.create_document_type')
      }
      enterButtonLabel={id ? i18n.t('common.update') : i18n.t('common.create')}
      privileges={{ action: 'update', subject: 'document' }}
      enterButtonProps={{ 'data-cy': 'save-document-type' }}
    >
      <Grid
        container
        direction="column"
        data-cy="create-document-type-modal"
        alignItems="flex-start"
      >
        <Box width="100%">
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={UpdateTimesSchema}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, errors }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box>
                      <Field
                        name="name"
                        component={TextField}
                        label={i18n.t('common.name')}
                        asyncErrors={errors?.name}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Field
                        name="id_slug"
                        required={false}
                        component={TextField}
                        label={i18n.t('common.id_slug')}
                        asyncErrors={errors?.id_slug}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </BaseModal>
  );
};

UpdateDocumentTypeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  documentType: PropTypes.any,
  handleClick: PropTypes.func.isRequired,
};

UpdateDocumentTypeModal.defaultProps = {
  open: false,
  documentType: null,
  handleClick: () => {},
};

export default UpdateDocumentTypeModal;
